import { memo, ReactNode, useCallback } from "react";
import { CircularProgress } from "@mui/material";
import { Virtuoso } from "react-virtuoso";
import { EmptyStateFlatlist } from "../EmptyStates/EmptyStateFlatlist";

type VirtuosoListProp = {
  items: any[];
  loadMoreOnPress: () => void;
  renderItem: (item: any, index: number) => ReactNode;
  isLoading: boolean;
  isFinished: boolean;
  searchTerm: string;
  renderNonSearchEmptyView: JSX.Element;
  renderHeader: JSX.Element | null;
  /** When provided, Virtuoso uses this element as the scroll container instead of creating its own. Enables sticky headers above the list. */
  customScrollParent?: HTMLElement | null;
};

const VirtuosoList = memo(function VirtuosoListFn(props: VirtuosoListProp) {
  const {
    items,
    loadMoreOnPress,
    renderItem,
    isLoading,
    isFinished,
    searchTerm,
    renderNonSearchEmptyView,
    renderHeader,
    customScrollParent,
  } = props;

  const EmptyPlaceholder = useCallback(() => {
    return (
      <EmptyStateFlatlist
        containerStyles={{ paddingTop: 200 }}
        isLoading={isLoading}
        searchTerm={searchTerm}
        nonSearchEmptyView={renderNonSearchEmptyView}
      />
    );
  }, [isLoading, searchTerm, renderNonSearchEmptyView]);

  const Footer = useCallback(() => {
    return items.length > 0 && isLoading && !isFinished ? (
      <CircularProgress
        style={{ color: "#929292", alignSelf: "center" }}
        size={20}
      />
    ) : (
      <div style={{ height: 40 }}></div>
    );
  }, [isLoading, isFinished, items.length]);

  const Header = useCallback(() => {
    return items.length > 0 && searchTerm === "" ? renderHeader : null;
  }, [items.length, searchTerm, renderHeader]);

  return (
    <Virtuoso
      style={customScrollParent ? { minHeight: "100%" } : { height: "100%" }}
      data={items}
      customScrollParent={customScrollParent ?? undefined}
      endReached={() => {
        if (searchTerm.trim() === "" && !isLoading && !isFinished) {
          loadMoreOnPress();
        }
      }}
      increaseViewportBy={200}
      itemContent={(index, item) => {
        return renderItem(item, index);
      }}
      components={{ Footer, EmptyPlaceholder, Header }}
    />
  );
});

export default VirtuosoList;
